/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { WebSocketSubject } from 'rxjs/webSocket';

import { MessageHandler } from './message-handler';
import environment from '../../../environments/environment';
import {
  selectConnectionId,
  selectIsConnected,
} from '../redux/websocket.selectors';
import {
  catchError,
  map,
  mergeMap,
  take,
  tap,
  filter,
  timeout,
  retry,
} from 'rxjs/operators';
import {
  combineLatest,
  defer,
  from,
  Observable,
  of,
  interval,
  timer,
  EMPTY,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectUser } from '@shared/store/selectors/user.selectors';
import { WebSocketActions } from '../redux/websocket.actions';
import { NewRelicService } from '@data-access/shared';

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private readonly _store = inject(Store);
  private readonly _messageHandler = inject(MessageHandler);
  private readonly _http = inject(HttpClient);
  private readonly _newRelic = inject(NewRelicService);

  private _socket$: WebSocketSubject<any> | undefined;
  private _activeSubscriptions = new Set<string>();
  private _connecting = false;
  private _reconnecting = false;
  isConnected = false;
  connectionId = '';
  readonly $userId = toSignal(
    this._store.select(selectUser).pipe(map((user) => user?.id ?? null)),
  );
  async connect(connectionId?: string) {
    if (this._socket$ && !this._socket$.closed) {
      console.log('WebSocket already connected.');
      return;
    }

    if (this._connecting) {
      console.log('WebSocket connection already in progress.');
      return;
    }

    this._connecting = true;

    if (!connectionId) {
      connectionId = this.generateConnectionId();
      this.connectionId = connectionId;
      this._store.dispatch(WebSocketActions.setConnectionId({ connectionId }));
    }

    this.getWebSocketUrl(connectionId).subscribe({
      complete: () => {
        console.log('WebSocket URL request complete');
        this._connecting = false;
      },
      error: (err) => {
        console.error('Failed to fetch WebSocket URL:', err);
        this._newRelic.trackEvent('WebSocketUrlError', { message: err });
        this._connecting = false;
      },
      next: (response) => {
        const webSocketUrl = response.url;
        if (!webSocketUrl) {
          this._connecting = false;
          return;
        }

        this._socket$ = new WebSocketSubject({
          closeObserver: {
            next: () => {
              setTimeout(() => {
                if (!this._socket$ || this._socket$.closed) {
                  this._store.dispatch(
                    WebSocketActions.webSocketDisconnected(),
                  );
                }
              }, 1000);
            },
          },
          openObserver: {
            next: () => {
              console.log('WebSocket connection opened');
              this.isConnected = true;
              this._store.dispatch(WebSocketActions.webSocketConnected());

              // Subscribe to user notifications AFTER WebSocket connection is established
              const userId = this.$userId();
              if (userId) {
                console.log(
                  'WebSocket connected, now subscribing to user notifications',
                );
                this._store.dispatch(
                  WebSocketActions.subscribeUserNotification({
                    userId: userId,
                  }),
                );
              }
            },
          },
          url: `${webSocketUrl}`,
        });

        this._socket$.subscribe({
          complete: () => {
            console.log('WebSocket connection complete');
            this.isConnected = false;
          },
          error: (err) => {
            console.error('WebSocket connection error:', err);
            this.isConnected = false;
            this._newRelic.trackEvent('WebSocketError', { message: err });
          },
          next: (message) => this._messageHandler.handleMessage(message),
        });
      },
    });
  }

  disconnect() {
    if (this._socket$) {
      console.log('Disconnecting WebSocket');
      this._socket$.complete();
      this._socket$ = undefined;
      this.isConnected = false;
    }
  }

  subscribeToWorkspace(
    workspaceId: string,
    reestablishConnection = false,
  ): Observable<string> {
    if (this._activeSubscriptions.has(workspaceId) && !reestablishConnection) {
      console.log(`Already subscribed to workspace: ${workspaceId}`);
      return of(workspaceId);
    }

    console.log(
      `Ensuring WebSocket connection before subscribing to workspace: ${workspaceId}`,
    );
    if (reestablishConnection) {
      this._reconnecting = reestablishConnection;
    }
    return this.ensureConnection().pipe(
      mergeMap((): Observable<string> => {
        if (
          !this._socket$ ||
          this._socket$.closed ||
          (!this.isConnected && !reestablishConnection)
        ) {
          console.error(
            'WebSocket is not connected. Attempting reconnect for workspace:',
            workspaceId,
          );
          return this.reconnectAndRetry(
            workspaceId,
            () => this.subscribeToWorkspace(workspaceId, true),
            workspaceId,
          );
        }

        this._activeSubscriptions.add(workspaceId);

        return combineLatest([this._store.select(selectConnectionId)]).pipe(
          take(1),
          mergeMap(([connectionId]) => {
            if (!connectionId) {
              console.error('Connection ID is null; skipping subscription.');
              this._activeSubscriptions.delete(workspaceId);
              return of(workspaceId);
            }

            console.log(
              `Subscribing to workspace with ID: ${workspaceId} and Connection ID: ${connectionId}`,
            );

            return this.workspaceSubscribe(workspaceId, connectionId).pipe(
              map(() => {
                this._store.dispatch(
                  WebSocketActions.setWorkspaceId({ workspaceId }),
                );
                console.log(`Subscribed to workspace: ${workspaceId}`);
                return workspaceId;
              }),
              catchError((error) => {
                console.error(
                  `Failed to subscribe to workspace ${workspaceId}:`,
                  error,
                );
                this._activeSubscriptions.delete(workspaceId);
                return of(workspaceId);
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        console.error('Failed to ensure WebSocket connection:', error);
        return of(workspaceId);
      }),
    );
  }

  unsubscribeFromWorkspace(workspaceId: string): Observable<void> {
    if (!this._activeSubscriptions.has(workspaceId)) {
      console.log(`No active subscription for workspace: ${workspaceId}`);
      return of();
    }

    this._activeSubscriptions.delete(workspaceId);
    console.log(`Unsubscribing from workspace: ${workspaceId}`);

    // Unsubscribing is done behind the scenes, but we still want to clean up the session storage item if it exists
    if (sessionStorage.getItem('submissionAccessRevoked') === workspaceId) {
      // cleanup the session storage item if it exists
      console.log(`Already unsubscribed from workspace: ${workspaceId}`);
      sessionStorage.removeItem('submissionAccessRevoked');
      return EMPTY;
    }

    return this._store.select(selectConnectionId).pipe(
      take(1),
      mergeMap((connectionId) => {
        const _initiatedLogout = sessionStorage.getItem('initiatedLogout');
        if (_initiatedLogout === 'true') {
          return EMPTY;
        }
        return this.workspaceUnsubscribe(workspaceId, connectionId).pipe(
          map(() => {
            this._store.dispatch(WebSocketActions.unsubscribeFromWorkspace());
          }),
        );
      }),
    );
  }

  private generateConnectionId() {
    return new Date().getTime().toString();
  }

  sendMessage(message: any) {
    if (this._socket$ && this.isConnected) {
      this._socket$.next(message);
    }
  }

  subscribeUserNotifications(userId: string): void {
    if (this._activeSubscriptions.has(userId)) {
      console.log(`Already subscribed to user: ${userId}`);
      return;
    }

    console.log(
      `Ensuring WebSocket connection before subscribing to user: ${userId}`,
    );

    this.ensureConnection().subscribe({
      next: () => {
        if (!this._socket$ || this._socket$.closed || !this.isConnected) {
          console.error(
            'WebSocket is not connected. Attempting reconnect for user:',
            userId,
          );
          this.reconnectAndRetry(
            userId,
            () => {
              this.subscribeUserNotifications(userId);
              return of(undefined);
            },
            undefined,
          ).subscribe();
          return;
        }
        this._activeSubscriptions.add(userId);
        this._store
          .select(selectConnectionId)
          .pipe(take(1))
          .subscribe({
            next: (connectionId) => {
              if (!connectionId) {
                console.error('Connection ID is null; skipping subscription.');
                this._activeSubscriptions.delete(userId);
                return;
              }

              console.log(
                `Subscribing to User with ID: ${userId} and Connection ID: ${connectionId}`,
              );

              this.userNotificationSubscribe(userId, connectionId).subscribe({
                next: () => {
                  this._store.dispatch(WebSocketActions.setUserId({ userId }));
                  console.log(`Subscribed to User: ${userId}`);
                },
                error: (error) => {
                  console.error(
                    `Failed to subscribe to User ${userId}:`,
                    error,
                  );
                  this._activeSubscriptions.delete(userId);
                },
              });
            },
            error: (error) => {
              console.error('Error fetching connection ID:', error);
            },
          });
      },
      error: (error) => {
        console.error('Failed to ensure WebSocket connection:', error);
      },
    });
  }

  unsubscribeUserNotifications(userId: string): Observable<void> {
    if (!this._activeSubscriptions.has(userId)) {
      console.log(`No active subscription for User: ${userId}`);
      return of();
    }

    this._activeSubscriptions.delete(userId);
    console.log(`Unsubscribing from User: ${userId}`);
    return this._store.select(selectConnectionId).pipe(
      take(1),
      mergeMap((connectionId) =>
        this.userNotificationUnsubscribe(userId, connectionId).pipe(
          map(() => {
            this._store.dispatch(WebSocketActions.unsubscribeFromUser());
          }),
        ),
      ),
    );
  }

  private getWebSocketUrl(connectionStr: string) {
    return this._http.get<{ url: string }>(
      `${environment.irisApi}/pubsub/connect/${connectionStr}/`,
    );
  }

  private workspaceSubscribe(workspaceId: string, connectionId: string) {
    return this._http.post(
      `${environment.irisApi}/pubsub/subscribe/workspace/${workspaceId}/${connectionId}/`,
      {},
    );
  }

  private workspaceUnsubscribe(workspaceId: string, connectionId: string) {
    return this._http.post(
      `${environment.irisApi}/pubsub/unsubscribe/workspace/${workspaceId}/${connectionId}/`,
      {},
    );
  }

  private userNotificationSubscribe(userId: string, connectionId: string) {
    return this._http.post(
      `${environment.irisApi}/pubsub/subscribe/user/${userId}/${connectionId}/`,
      {},
    );
  }

  private userNotificationUnsubscribe(userId: string, connectionId: string) {
    return this._http.post(
      `${environment.irisApi}/pubsub/unsubscribe/user/${userId}/${connectionId}/`,
      {},
    );
  }

  private reconnectAndRetry<T>(
    label: string,
    retryFn: () => Observable<T>,
    fallback: T,
    maxRetries = 3,
  ): Observable<T> {
    this._newRelic.trackEvent('WebSocketReconnectRequired', { label });
    console.warn(
      `WebSocket not connected — reconnecting for: ${label} (up to ${maxRetries} retries)`,
    );

    return defer(() => {
      this._store.dispatch(WebSocketActions.connectWebSocket());
      return this._store.select(selectIsConnected).pipe(
        filter((connected) => connected),
        take(1),
        timeout(5000),
        mergeMap(() => retryFn()),
      );
    }).pipe(
      retry({
        count: maxRetries,
        delay: (_, retryCount) => {
          const backoffMs = Math.pow(2, retryCount - 1) * 1000; // 1s, 2s, 4s
          console.warn(
            `WebSocket reconnect retry ${retryCount}/${maxRetries} for: ${label} — next attempt in ${backoffMs}ms`,
          );
          return timer(backoffMs);
        },
      }),
      catchError(() => {
        console.error(
          `WebSocket reconnect failed after ${maxRetries} retries for: ${label}`,
        );
        return of(fallback);
      }),
    );
  }

  private ensureConnection(): Observable<void> {
    if (this._socket$ && !this._socket$.closed) {
      return of(undefined);
    }

    if (this._reconnecting) {
      return interval(100).pipe(
        filter(() => this._socket$ !== undefined && !this._socket$.closed),
        take(1),
        map(() => undefined),
        timeout(5000), // Add a timeout to prevent infinite waiting
        catchError((error) => {
          console.error('Error during WebSocket reconnection:', error);
          return of(undefined);
        }),
      );
    }

    this._reconnecting = true;
    this._connecting = true;
    console.log('WebSocket connection is closed. Reinitializing...');

    const connectionId = this.generateConnectionId();

    return from(this.connect(connectionId)).pipe(
      tap(() => {
        this._reconnecting = false;
        this._connecting = false;
      }),
      catchError((error) => {
        console.error(
          'Unexpected error during WebSocket initialization:',
          error,
        );
        return of(undefined);
      }),
    );
  }
}
